import React, { useState, useEffect, useRef } from "react";
import { BrowserRouter, Route, Redirect, Switch } from "react-router-dom";
import "react-notifications/lib/notifications.css";
import { NotificationContainer } from "react-notifications";
import Login from "./Auth/Login";
import Home from "./Components/Home";
import PrivateRoute from "./PrivateRoute";
import PrivateDiv from "./PrivateDiv";
import AdminRoute from "./AdminRoute";
import LoginContextProvider from "./Auth/LoginContextProvider";
import UserContextProvider from "./Auth/UserContextProvider";
import FilterContextProvider from "./FilterContextProvider";
import RequestPasswordReset from "./Auth/RequestPasswordReset";
import ResetPassword from "./Auth/ResetPassword";
import ManageInventory from "./Inventory/ManageInventory";
import AdjustInventory from "./Inventory/AdjustInventory";
import ForecastInventory from "./Inventory/ForecastInventory";
import LocationInventory from "./Inventory/LocationInventory";
import InventoryHistory from "./Inventory/InventoryHistory";
import ProductHistory from "./Inventory/ProductHistory";
import EventsList from "./Sales/EventsList";
import EventComparison from "./Sales/EventComparison";
import EventDetails from "./Sales/EventDetails";
import TonightsProducts from "./Sales/TonightsProducts";
import EventSales from "./Sales/EventSales";
import EventSummary from "./Sales/EventSummary";
import WebSales from "./Sales/WebSales";
import WebSalesHistory from "./Sales/WebSalesHistory";
import CompsList from "./Sales/CompsList";
import CreateComps from "./Sales/CreateComps";
import CompDetails from "./Sales/CompDetails";
import Artists from "./Artists/Artists";
import ArtistDetails from "./Artists/ArtistDetails";
import Reports from "./Reports/Reports";
import Partnership from "./Reports/Partnership";
import PartnershipNew from "./Reports/PartnershipNew";
import COGS from "./Reports/COGS";
import Promo from "./Reports/Promo";
import EventSalesReport from "./Reports/EventSalesReport";
import SalesSheet from "./Reports/SalesSheet";
import SalesSheetEventsList from "./Reports/SalesSheetEventsList";
import InventoryValue from "./Reports/InventoryValue";
import ProductInventory from "./Reports/ProductInventory";
import ReportEventsList from "./Reports/ReportEventsList";
import Tour from "./Reports/Tour";
import AllProducts from "./Reports/AllProducts";
import AllProductsNew from "./Reports/AllProductsNew";
import ProductTotals from "./Reports/ProductTotals";
import CtmSales from "./Reports/CtmSales";
import Taxes from "./Reports/Taxes";
import Deposits from "./Reports/Deposits";
import PaymentsDueCtm from "./Reports/PaymentsDueCtm";
import PaymentsDueVenue from "./Reports/PaymentsDueVenue";
import AccountsPayable from "./Reports/AccountsPayable";
import AccountsReceivable from "./Reports/AccountsReceivable";
import VendorPayroll from "./Reports/VendorPayroll";
import Cash from "./Reports/Cash";
import ProductSales from "./Reports/ProductSales";
import ProductTotalsSales from "./Reports/ProductTotalsSales";
import ArtistsPerformance from "./Reports/ArtistsPerformance";
import Users from "./Users/Users";
import UserDetails from "./Users/UserDetails";
import EditUser from "./Forms/EditUser";
import SideBar from "./Components/SideBar";
import FilterBar from "./Components/FilterBar";
import "./App.css";
import ManagerRoute from "./ManagerRoute";
import ErrorBoundary from "./Components/ErrorBoundary";
import { ManualOfflineContext } from "./Sales/ManualOfflineContext";
import OnlineOfflineSwitcher from "./Sales/OnlineOfflineSwitcher";
import config from "./Config";

const App = () => {
  const [showFilterBar, setShowFilterBar] = useState(false);
  const drawerRef = useRef();
  const [manualOffline, setManualOffline] = useState(false);

  const handleShowFilterBar = () => {
    setShowFilterBar(!showFilterBar);
  };

  useEffect(() => {
    const checkIfClickedOutside = (e) => {
      if (showFilterBar && drawerRef.current && !drawerRef.current.contains(e.target)) {
        setShowFilterBar(false);
      }
    };

    document.addEventListener("mousedown", checkIfClickedOutside);

    return () => {
      document.removeEventListener("mousedown", checkIfClickedOutside);
    };
  }, [showFilterBar]);

  return (
    <div className="font-sans h-screen bg-gray-100 text-black tracking-wide">
      <ErrorBoundary>
        <LoginContextProvider>
          <UserContextProvider>
            <FilterContextProvider>
              <OnlineOfflineSwitcher pollingUrl={config.api.healthcheckUrl}>
                <ManualOfflineContext.Provider value={{ manualOffline, setManualOffline }}>
                  <BrowserRouter>
                    <PrivateDiv className="fixed mr-24">
                      <SideBar showFilterBar={showFilterBar} handleShowFilterBar={handleShowFilterBar}></SideBar>
                    </PrivateDiv>
                    <PrivateDiv className="fixed mr-24"></PrivateDiv>
                    <PrivateDiv>
                      {showFilterBar && (
                        <div ref={drawerRef}>
                          <FilterBar handleShowFilterBar={handleShowFilterBar}></FilterBar>
                        </div>
                      )}
                    </PrivateDiv>
                    <ErrorBoundary>
                      <Switch>
                        <Route path="/login" render={() => <Login />} />
                        <Route exact path="/requestpassword" component={RequestPasswordReset} />
                        <Route exact path="/users/password/edit/" render={() => <ResetPassword />} />
                        <PrivateRoute exact path="/home" component={Home} />
                        <AdminRoute exact path="/inventory/adjust" component={AdjustInventory} />
                        <PrivateRoute exact path="/inventory/forecast" component={ForecastInventory} />
                        <PrivateRoute exact path="/inventory/location" component={LocationInventory} />
                        <PrivateRoute exact path="/inventory/history" component={InventoryHistory} />
                        <PrivateRoute exact path="/inventory/manage" component={ManageInventory} />
                        <AdminRoute exact path="/inventory/history/:productId/" component={ProductHistory} />
                        <PrivateRoute exact path="/sales/events" component={EventsList} />
                        <ManagerRoute exact path="/sales/eventcomparison" component={EventComparison} />
                        <PrivateRoute exact path="/sales/events/:eventId/details/" component={EventDetails} />
                        <PrivateRoute exact path="/sales/events/:eventId/tonight/" component={TonightsProducts} />
                        <PrivateRoute exact path="/sales/events/:eventId/sales/" component={EventSales} />
                        <PrivateRoute exact path="/sales/events/:eventId/summary/" component={EventSummary} />
                        <PrivateRoute exact path="/sales/websales/view" component={WebSales} />
                        <PrivateRoute exact path="/sales/websales/view/:year/:month" component={WebSales} />
                        <PrivateRoute exact path="/sales/websales/history" component={WebSalesHistory} />
                        <PrivateRoute exact path="/sales/comps/list" component={CompsList} />
                        <PrivateRoute exact path="/sales/comps/:eventId/details/" component={CompDetails} />
                        <PrivateRoute exact path="/sales/comps/create" component={CreateComps} />
                        <PrivateRoute exact path="/artists/manage" component={Artists} />
                        <PrivateRoute exact path="/artists/:artistId/" component={ArtistDetails} />
                        <PrivateRoute exact path="/reports" component={Reports} />
                        <AdminRoute exact path="/reports/partnership" component={Partnership} />
                        <AdminRoute exact path="/reports/partnership_new" component={PartnershipNew} />
                        <AdminRoute exact path="/reports/cogs" component={COGS} />
                        <AdminRoute exact path="/reports/promo" component={Promo} />
                        <PrivateRoute exact path="/reports/events/:eventId/sales/" component={EventSalesReport} />
                        <PrivateRoute exact path="/reports/events/:eventId/salessheet/" component={SalesSheet} />
                        <PrivateRoute exact path="/reports/events" component={ReportEventsList} />
                        <PrivateRoute exact path="/reports/salessheet" component={SalesSheetEventsList} />
                        <PrivateRoute exact path="/reports/inventoryvalue" component={InventoryValue} />
                        <PrivateRoute exact path="/reports/productinventory" component={ProductInventory} />
                        <PrivateRoute exact path="/reports/tour" component={Tour} />
                        <PrivateRoute exact path="/reports/sales" component={AllProducts} />
                        <PrivateRoute exact path="/reports/sales_new" component={AllProductsNew} />
                        <PrivateRoute exact path="/reports/producttotals" component={ProductTotals} />
                        <PrivateRoute exact path="/reports/ctm-sales" component={CtmSales} />
                        <PrivateRoute exact path="/reports/artists-performance" component={ArtistsPerformance} />
                        <AdminRoute exact path="/reports/taxes" component={() => <Taxes isCtmTaxable={false} />} />
                        <AdminRoute exact path="/reports/ctmtaxes" component={() => <Taxes isCtmTaxable />} />
                        <AdminRoute exact path="/reports/deposits" component={Deposits} />
                        <AdminRoute exact path="/reports/paymentsctm" component={PaymentsDueCtm} />
                        <AdminRoute exact path="/reports/paymentsvenue" component={PaymentsDueVenue} />
                        <AdminRoute exact path="/reports/accountspayable" component={AccountsPayable} />
                        <AdminRoute exact path="/reports/accountsreceivable" component={AccountsReceivable} />
                        <AdminRoute exact path="/reports/payroll" component={VendorPayroll} />
                        <AdminRoute exact path="/reports/totalpayroll" component={() => <VendorPayroll isTotalPayroll />} />
                        <AdminRoute exact path="/reports/cash" component={Cash} />
                        <AdminRoute exact path="/reports/sales/:productId" component={ProductSales} />
                        <AdminRoute exact path="/reports/sales/totals/:productId" component={ProductTotalsSales} />
                        <AdminRoute exact path="/users/:userId/" component={UserDetails} />
                        <AdminRoute exact path="/users" component={Users} />
                        <AdminRoute exact path="/users/:userId/edit" component={EditUser} />
                        <Redirect from="/inventory" to="/inventory/forecast" />
                        <Redirect from="/sales/websales/" to="/sales/websales/view" />
                        <Redirect from="/sales/comps/" to="/sales/comps/list" />
                        <Redirect from="/sales" to="/sales/events" />
                        <Redirect from="/artists" to="/artists/manage" />
                        <Redirect from="/" to="/home" />
                      </Switch>
                    </ErrorBoundary>
                  </BrowserRouter>
                </ManualOfflineContext.Provider>
              </OnlineOfflineSwitcher>
            </FilterContextProvider>
          </UserContextProvider>
        </LoginContextProvider>
      </ErrorBoundary>
      <NotificationContainer />
    </div>
  );
};

export default App;
